import { useRef } from 'react';
import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { Avatar, Button, Text, tokens } from '@hotjar/hotsauce';
import { CollectionActions } from '@hotjar/webapp/features/Collections/CollectionActions';
import { useWidthChanged } from '@hotjar/insights-common';
import { useCurrentSiteId } from '@hotjar/site';
import { useUserInfos } from '@hotjar/webapp/features/Highlights/List/use-user-infos';
import { trackCollectionShared } from '@hotjar/webapp/domains/collections/api';
import type { Collection } from '@hotjar/webapp/domains/collections/model';
import { ALL_HIGHLIGHTS_ID } from '@hotjar/webapp/domains/collections/model';
import { SharingModule } from '@hotjar/webapp/components/SharingModule/SharingModule';

const SMALL_HEADER_SIZE_THRESHOLD = 970;

const Header = styled.div``;

const CollectionInfos = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AuthorInfos = styled.div`
  color: ${tokens.color.text_light};
  border-right: 1px solid ${tokens.color.ui_border_light};
  height: 24px;
  padding-right: ${tokens.spacer.x2};
  margin-right: ${tokens.spacer.x2};
  display: flex;
  align-items: center;
  flex-shrink: 1;
  min-width: 0px;
`;

const TitleContainer = styled.div`
  flex-shrink: 1;
  overflow: hidden;
  max-width: 55%;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Title = styled(Text)`
  display: block;
  padding-bottom: ${tokens.spacer.x0pt5};
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0px;
`;

const UserAvatar = styled(Avatar)`
  margin-right: ${tokens.spacer.x0pt5};
  flex-shrink: 0;
`;

const AuthorName = styled.span`
  margin-right: ${tokens.spacer.x0pt5};
  white-space: nowrap;
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShareButton = styled(Button)`
  margin-left: ${tokens.spacer.x1};
`;

const DateCreated = styled.span`
  margin-left: ${tokens.spacer.x0pt5};
`;

function getDateString(date: number | undefined) {
  return date
    ? new Date(date * 1000).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';
}

interface HighlightsHeaderProps {
  collection: Omit<Collection, 'highlightsCount'>;
  newCollectionButton: ReactNode;
}

export const HighlightsHeader = ({
  collection,
  newCollectionButton,
}: HighlightsHeaderProps): JSX.Element => {
  const currentSiteId = useCurrentSiteId();
  const { data: collectionCreator } = useUserInfos(collection.userId ?? null);
  const { t } = useTranslation();

  const headerRef = useRef<HTMLDivElement | null>(null);

  const [headerWidth] = useWidthChanged(headerRef);

  const handleOnShare = () => {
    if (currentSiteId) {
      trackCollectionShared(currentSiteId, collection.id);
    }
  };

  return (
    <Header ref={headerRef}>
      <CollectionInfos>
        <TitleContainer>
          <Title
            as={headerWidth < SMALL_HEADER_SIZE_THRESHOLD ? 'h5' : 'h3'}
            textType={headerWidth < SMALL_HEADER_SIZE_THRESHOLD ? 'header5' : 'header3'}
          >
            {collection.keyName
              ? t(collection.keyName)
              : collection.name ?? t('highlights:list.title')}
          </Title>
        </TitleContainer>
        <HeaderActions>
          {collection.created && (
            <AuthorInfos>
              <UserAvatar
                userName={collection.userName ?? ''}
                src={collectionCreator?.avatar ?? undefined}
                size="s"
              />
              <AuthorName>{collection.userName}</AuthorName>·
              <DateCreated>{getDateString(collection.created)}</DateCreated>
            </AuthorInfos>
          )}
          {collection.id !== ALL_HIGHLIGHTS_ID && (
            <CollectionActions collection={collection} siteId={currentSiteId} compact isVisible />
          )}
          {newCollectionButton}
          <SharingModule
            trigger={
              <ShareButton kind="primary" size="s">
                {t('common:actions.share')}
              </ShareButton>
            }
            onShare={handleOnShare}
          />
        </HeaderActions>
      </CollectionInfos>
    </Header>
  );
};
